(function (root) {
  'use strict';
  const NAME = 'thecuplab-set-drafts-v1';
  const STORE = 'drafts';
  const TTL = 48 * 60 * 60 * 1000;
  const queues = new Map();
  let opening;

  function validKey(key) {
    if (typeof key !== 'string' || !/^cupset:[A-Za-z0-9:_-]{1,200}$/.test(key)) throw new Error('Invalid draft key');
    return key;
  }

  function open() {
    if (opening) return opening;
    opening = new Promise((resolve, reject) => {
      if (!root.indexedDB) { reject(new Error('Draft storage unavailable')); return; }
      let finished = false;
      const request = root.indexedDB.open(NAME, 1);
      const timer = setTimeout(() => {
        if (finished) return;
        finished = true;
        reject(new Error('Draft storage unavailable'));
      }, 2500);
      request.onupgradeneeded = () => {
        const db = request.result;
        if (!db.objectStoreNames.contains(STORE)) {
          const store = db.createObjectStore(STORE, { keyPath: 'key' });
          store.createIndex('expiresAt', 'expiresAt');
        }
      };
      request.onerror = () => {
        if (finished) return;
        finished = true;
        clearTimeout(timer);
        reject(request.error || new Error('Draft storage unavailable'));
      };
      request.onsuccess = () => {
        const db = request.result;
        if (finished) { db.close(); return; }
        finished = true;
        clearTimeout(timer);
        db.onversionchange = () => { db.close(); opening = null; };
        const tx = db.transaction(STORE, 'readwrite');
        const cursor = tx.objectStore(STORE).index('expiresAt').openCursor(root.IDBKeyRange.upperBound(Date.now()));
        cursor.onsuccess = () => { if (cursor.result) { cursor.result.delete(); cursor.result.continue(); } };
        tx.oncomplete = () => resolve(db);
        tx.onabort = tx.onerror = () => resolve(db);
      };
    }).catch(error => { opening = null; throw error; });
    return opening;
  }

  async function transaction(mode, action) {
    const db = await open();
    return new Promise((resolve, reject) => {
      const tx = db.transaction(STORE, mode);
      let result;
      try { result = action(tx.objectStore(STORE)); }
      catch (error) { tx.abort(); reject(error); return; }
      tx.oncomplete = () => resolve(result?.result);
      tx.onabort = tx.onerror = () => reject(tx.error || result?.error || new Error('Draft storage failed'));
    });
  }

  function queued(key, action) {
    validKey(key);
    const previous = queues.get(key) || Promise.resolve();
    const pending = previous.catch(() => {}).then(action);
    queues.set(key, pending);
    const clean = () => { if (queues.get(key) === pending) queues.delete(key); };
    pending.then(clean, clean);
    return pending;
  }

  const api = {
    save(key, snapshot) {
      let copy;
      try { copy = root.structuredClone(snapshot); }
      catch (error) { return Promise.reject(error); }
      return queued(key, () => {
        const now = Date.now();
        return transaction('readwrite', store => store.put({ key, version: 1, updatedAt: now, expiresAt: now + TTL, snapshot: copy }));
      });
    },
    load(key) {
      return queued(key, async () => {
        const record = await transaction('readonly', store => store.get(key));
        if (!record) return null;
        if (record.version !== 1 || !Number.isFinite(record.expiresAt) || !Number.isFinite(record.updatedAt) || record.expiresAt <= Date.now() || record.expiresAt > record.updatedAt + TTL || !record.snapshot || typeof record.snapshot !== 'object') {
          await transaction('readwrite', store => store.delete(key));
          return null;
        }
        return { snapshot: record.snapshot, updatedAt: record.updatedAt, expiresAt: record.expiresAt };
      });
    },
    remove(key) { return queued(key, () => transaction('readwrite', store => store.delete(key))); },
    attach(editor) {
      if (!editor?.host || editor.draftStorage) return;
      const host = editor.host;
      const editKey = new URL(root.location.href).searchParams.get('cup_edit') || 'new';
      let key;
      try { key = validKey('cupset:' + host.product.id + ':' + host.stickerTemplate + ':' + editKey); }
      catch { return; }
      const box = root.document.createElement('div');
      box.className = 'cup-set-draft';
      const status = root.document.createElement('small');
      status.setAttribute('role', 'status');
      const actions = root.document.createElement('div');
      const restoreButton = root.document.createElement('button');
      restoreButton.type = 'button';
      restoreButton.textContent = editor.t('draftRestore');
      const removeButton = root.document.createElement('button');
      removeButton.type = 'button';
      removeButton.textContent = editor.t('draftRemove');
      actions.append(restoreButton, removeButton);
      actions.hidden = true;
      box.append(status, actions);
      editor.node.appendChild(box);
      let timer, existing = null, blocked = true, restoring = false, modified = false, revision = 0;
      const state = editor.draftStorage = { key, flush: () => persist() };
      const message = text => { status.textContent = editor.t(text); };

      function snapshot() {
        const file = host.artworkFile || host.upload.files[0];
        const active = file && host.artwork && !host.imageLoading ? {
          id: editor.editId, style: editor.model.style, count: Number(editor.input.value) || 1,
          sourceFile: file, fields: {
            Datei: file.name, Text: host.textInput?.value || '',
            Songtitel: host.textInput?.value || '', Interpret: host.artistInput?.value || '',
            Bildbeschreibung: host.textInput?.value || '', Hashtags: host.socialHashtagsInput?.value || ''
          },
          background: host.frameColor,
          parameters: {
            style: host.stickerTemplate, version: 2, width_mm: 46, height_mm: 72,
            background: host.frameColor, x: host.socialCrop?.x || 0, y: host.socialCrop?.y || 0,
            zoom: host.socialCrop?.zoom || 1, rotation: host.artworkTransform?.rotation || 0
          }
        } : null;
        return {
          version: 1, productId: String(host.product.id), style: editor.model.style,
          variantId: String(host.variantInput.value), total: editor.model.total,
          enabled: editor.enabled, dirty: editor.dirty, active,
          designs: editor.model.designs.map(({ thumb, ...design }) => design)
        };
      }

      async function persist() {
        clearTimeout(timer);
        if (blocked || restoring || !modified || !editor.enabled) return;
        if (editor.busy || host.imageLoading) { timer = setTimeout(persist, 400); return; }
        try {
          const saved = snapshot(), savingRevision = revision;
          if (!saved.designs.length && !saved.active) return;
          await api.save(key, saved);
          modified = revision !== savingRevision;
          message('draftSaved');
          if (modified) { clearTimeout(timer); timer = setTimeout(persist, 400); }
        } catch { message('draftUnavailable'); }
      }

      function schedule() {
        if (restoring) return;
        revision++;
        modified = true;
        clearTimeout(timer);
        timer = setTimeout(persist, 400);
      }

      function validFile(value, kind) {
        if (value instanceof File) {
          const types = kind === 'print' ? ['image/png'] : ['image/png', 'image/jpeg', 'image/webp'];
          return value.size > 0 && value.size <= 19 * 1024 * 1024 && types.includes(value.type);
        }
        try {
          const url = new URL(typeof value === 'string' ? value : value?.url);
          if (url.protocol !== 'https:' || url.hostname !== 'cdn.shopify.com' || url.username || url.password) return false;
          if (typeof value === 'string') return true;
          return /^M\d{2}-(druck\.png|foto\.(png|jpg|webp))$/.test(value.entry || '') && (kind === 'print' ? value.type === 'image/png' : ['image/png', 'image/jpeg', 'image/webp'].includes(value.type));
        } catch { return false; }
      }

      host.addEventListener('input', schedule);
      host.addEventListener('change', schedule);
      host.addEventListener('pointerup', schedule);
      host.addEventListener('click', schedule);
      root.addEventListener('pagehide', () => { persist(); });
      const save = editor.save.bind(editor);
      editor.save = async (...args) => { const result = await save(...args); schedule(); return result; };

      removeButton.addEventListener('click', async () => {
        if (restoring) return;
        try {
          await api.remove(key); existing = null; blocked = false; actions.hidden = true;
          message('draftRemoved'); modified = false;
        } catch { message('draftUnavailable'); }
      });

      restoreButton.addEventListener('click', async () => {
        if (restoring || !existing || editor.busy || host.purchaseBusy) return;
        if ((editor.dirty || editor.model.designs.length) && !root.confirm(editor.t('discard'))) return;
        restoring = true;
        restoreButton.disabled = removeButton.disabled = true;
        const previousModel = editor.model, previousEnabled = editor.enabled;
        const previousSnapshot = snapshot(), previousEditId = editor.editId, previousDirty = editor.dirty;
        const previousSelected = new Set(editor.selected), previousInput = editor.input.value;
        let changing = false;
        const picker = host.closest('product-info')?.querySelector('variant-selects') || root.document.querySelector('variant-selects');
        const controls = picker ? [...picker.querySelectorAll('select, input[type=radio]')].map(input => ({input, value: input.value, checked: input.checked})) : [];
        try {
          const saved = existing.snapshot;
          if (saved.version !== 1 || saved.productId !== String(host.product.id) || saved.style !== host.stickerTemplate || !Array.isArray(saved.designs) || saved.designs.length > 60) throw new Error('Invalid draft');
          const variant = host.product.variants.find(v => String(v.id) === saved.variantId);
          if (!variant) throw new Error('Invalid variant');
          const model = new root.TheCupLabSetModel(saved.total, saved.style);
          const ids = new Set();
          for (const design of saved.designs) {
            if (ids.has(design.id)) throw new Error('Duplicate design');
            ids.add(design.id);
            if (!design.fields || !design.parameters || design.parameters.style !== saved.style || !/^#[a-f0-9]{6}$/i.test(design.background || '') || !validFile(design.sourceFile, 'source') || !validFile(design.printFile, 'print')) throw new Error('Invalid design');
            model.upsert({ ...design, thumb: '' });
          }
          if (saved.active && (saved.active.style !== saved.style || saved.active.parameters?.style !== saved.style || !saved.active.fields || !/^#[a-f0-9]{6}$/i.test(saved.active.background || '') || !validFile(saved.active.sourceFile, 'source'))) throw new Error('Invalid active draft');
          for (const design of model.designs) {
            if (design.previewFile instanceof Blob) {
              design.thumb = URL.createObjectURL(design.previewFile); editor.urls.add(design.thumb);
            } else if (typeof design.previewFile === 'string') {
              const url = new URL(design.previewFile);
              if (url.protocol !== 'https:' || url.hostname !== 'cdn.shopify.com') throw new Error('Invalid preview');
              design.thumb = url.href;
            } else throw new Error('Invalid preview');
          }
          changing = true;
          if (picker) picker.querySelectorAll('fieldset, .product-form__input').forEach((group, i) => {
            const value = variant.options?.[i] || variant['option' + (i + 1)], select = group.querySelector('select');
            if (select) select.value = value;
            else group.querySelectorAll('input[type=radio]').forEach(input => { input.checked = input.value === value; });
          });
          host.variantInput.value = variant.id;
          host.syncSelectedVariant();
          if (editor.total() !== saved.total) throw new Error('Invalid total');
          editor.model = model; editor.enabled = true; editor.dirty = false; editor.editId = null; editor.selected.clear();
          editor.update();
          if (saved.active) {
            await editor.load({ ...saved.active, id: saved.active.id || '__draft_active' });
            editor.editId = model.designs.some(d => d.id === saved.active.id) ? saved.active.id : null;
            editor.dirty = Boolean(saved.dirty || !editor.editId);
          } else if (model.designs.length) await editor.load(model.designs[0]);
          if (host.consentInput) host.consentInput.checked = false;
          editor.update();
          existing = null; blocked = false; actions.hidden = true; modified = false;
          message('draftRestored');
        } catch {
          editor.model = previousModel; editor.enabled = previousEnabled;
          if (changing) {
            try {
              for (const control of controls) { control.input.value = control.value; if (typeof control.checked === 'boolean') control.input.checked = control.checked; }
              host.variantInput.value = previousSnapshot.variantId;
              host.syncSelectedVariant();
              editor.dirty = false;
              if (previousSnapshot.active) await editor.load({ ...previousSnapshot.active, id: previousEditId || '__previous_active' });
              else await editor.newDraft(true);
              editor.editId = previousEditId; editor.dirty = previousDirty;
            } catch { editor.restoreError = true; }
          }
          editor.selected = previousSelected; editor.input.value = previousInput;
          editor.update();
          message('draftRestoreFailed');
        } finally {
          restoring = false; restoreButton.disabled = removeButton.disabled = false;
        }
      });

      host.productForm.addEventListener('product-form:complete', event => {
        if (event.detail?.error !== false) { schedule(); return; }
        clearTimeout(timer); modified = false; blocked = true;
        api.remove(key).then(() => { actions.hidden = true; status.textContent = ''; }).catch(() => {});
      });
      api.load(key).then(record => {
        existing = record;
        if (record) { actions.hidden = false; message('draftFound'); }
        else { blocked = false; if (modified) schedule(); }
      }).catch(() => { blocked = false; message('draftUnavailable'); });
      return state;
    }
  };
  root.TheCupLabDrafts = api;
})(typeof window !== 'undefined' ? window : globalThis);
